import { addBookmarkedArticle } from '../articles/services/ArticleService';
import { isUserLoggedIn, openLoginModal } from './Auth';
import { subscribeToAuthorOrTopic, subscribeToNewsletter } from '..//newsletter/services/NewsletterService';

export type UserAction = 'bookmark' | 'follow-author' | 'follow-topic' | 'newsletter';

/*
  If the user is not logged in, the action is stored in local storage for later execution (after the user logs in or registers) and returns false.
  If the user is already logged in, the function returns true, allowing the regular logic to proceed when called.
 */
export const handleUserActionWithLoginCheck = (
  userAction: UserAction,
  id: number | string,
  isPremiumNewsletter?: boolean
): boolean => {
  if (!isUserLoggedIn()) {
    localStorage.setItem(
      'pendingAction',
      JSON.stringify({
        actionType: userAction,
        id: id,
      })
    );

    // Store the pending premium newsletter action in local storage
    if (isPremiumNewsletter) {
      localStorage.setItem('pendingPremiumNewsletterAction', id.toString());
    }

    // Store the scroll position before navigating or redirecting
    const scrollPosition = window.scrollY || window.pageYOffset;
    localStorage.setItem('redirectScrollPosition', scrollPosition.toString());

    openLoginModal(userAction);

    return false;
  }

  return true;
};

// function is called on login/register if there is pending action in local storage (bookmark, follow author, follow topic, newsletter)
export const onLoginSuccess = async (userAction: UserAction, id: number) => {
  // to store local storage key whether action was successful or not and display snackbar message later
  const successMessage = `${userAction}-success`;
  const errorMessage = `${userAction}-error`;

  try {
    switch (userAction) {
      case 'bookmark':
        await addBookmarkedArticle(id.toString());
        localStorage.setItem('snackbarMessage', successMessage);
        break;
      case 'follow-author':
        await subscribeToAuthorOrTopic('authors', id);
        localStorage.setItem('snackbarMessage', successMessage);
        break;
      case 'follow-topic':
        await subscribeToAuthorOrTopic('topics', id);
        localStorage.setItem('snackbarMessage', successMessage);
        break;
      case 'newsletter':
        // this is for premium newsletters and we don't know yet if user will be premium or not after login
        if (!localStorage.getItem('pendingPremiumNewsletterAction')) {
          // we execute the sub action if user is premium, otherwise we open modal later in useEffect on initial page load
          await subscribeToNewsletter(id.toString());
          localStorage.setItem('snackbarMessage', successMessage);
        }
        break;
      default:
        localStorage.setItem('snackbarMessage', 'Error performing action.');
        break;
    }
  } catch (error) {
    // Handle any errors that occurred during the async operations
    console.error('Error in onLoginSuccess:', error);
    localStorage.setItem('snackbarMessage', errorMessage);
  } finally {
    // Ensure that the pending action is always cleared
    localStorage.removeItem('pendingAction');
  }
};
